import { Typography } from '@material-tailwind/react';
import {
  FREE_TRIAL_RESULT_TYPE_MAP,
  FreeTrialRecitationResult,
} from '../../constants/types/free-trial/type';
import { copulaParticle, removeSurname } from '../../api/utils/util-func';

interface FreeTrialResultCardProps {
  freeTrialRecitationResult: FreeTrialRecitationResult;
}

const FreeTrialResultCard = ({
  freeTrialRecitationResult,
}: FreeTrialResultCardProps) => {
  const { childName, recitationType, totalScore } = freeTrialRecitationResult;

  const result = FREE_TRIAL_RESULT_TYPE_MAP[recitationType];

  return (
    <div
      id="free-trial-result-card"
      className="relative mx-auto w-full max-w-[360px] overflow-hidden rounded-[28px] p-5 md:p-6"
      style={{
        backgroundColor: result.backgroundColor,
      }}
    >
      {/* 우측 상단 장식 */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-12 h-[180px] w-[210px] opacity-[0.08]"
        style={{
          backgroundColor: result.accentColor,
          borderRadius: '38% 0 0 72% / 62% 0 0 48%',
        }}
      />

      <div className="relative z-10">
        {/* 카드 상단 */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <img
              src={process.env.PUBLIC_URL + '/images/logo.png'}
              alt="JEBS 로고"
              className="h-7 w-auto object-contain md:h-8"
            />
          </div>

          <Typography className="text-right text-[9px] font-bold text-gray-700 md:text-[10px]">
            바이블 스토리 암송 · AI 스피킹 인증
          </Typography>
        </div>

        {/* 유형 */}
        <div className="mt-6 text-center">
          <Typography className="text-[11px] font-semibold text-gray-700 md:text-[12px]">
            {copulaParticle(removeSurname(childName))}의 암송 캐릭터는?
          </Typography>

          <Typography
            data-capture-role="catchphrase"
            className="mt-2 text-[12px] font-extrabold"
            style={{ color: result.accentColor }}
          >
            {result.catchphrase} 아이
          </Typography>

          <Typography
            data-capture-role="result-name"
            className="mt-1 text-[38px] font-black leading-none text-gray-900 md:text-[42px]"
          >
            {result.name}
          </Typography>
        </div>

        {/* 캐릭터 */}
        <div
          data-capture-role="character-image-wrap"
          className="mt-5 flex justify-center"
        >
          <img
            src={process.env.PUBLIC_URL + result.image}
            alt={result.name}
            className="h-[130px] w-[130px] object-contain"
          />
        </div>

        {/* 설명 */}
        <Typography className="mt-5 whitespace-pre-line text-center text-[11px] font-medium leading-5 text-gray-700 md:text-[12px]">
          {result.description}
        </Typography>

        <Typography
          data-capture-role="verse"
          className="mt-2 text-center text-[9px] font-medium leading-5 text-gray-700 md:text-[10px]"
        >
          {result.verse}
        </Typography>

        {/* 태그 */}
        <div
          data-capture-role="tags-wrap"
          className="mt-1 flex flex-wrap justify-center gap-2"
        >
          {result.tags.map((item) => (
            <span
              key={item}
              className="rounded-full bg-white px-2.5 py-1 text-[9px] font-bold md:text-[10px]"
              style={{
                color: result.accentColor,
              }}
            >
              <span data-capture-offset="-6">{item}</span>
            </span>
          ))}
        </div>

        {/* 오늘의 말씀 */}
        <div className="mt-4 rounded-2xl bg-white px-4 py-3 text-center">
          <Typography className="text-[10px] font-extrabold text-gray-800 md:text-[11px]">
            <span data-capture-offset="-7">
              오늘 {copulaParticle(removeSurname(childName))}의 마음에 영어로
              말씀을 심었어요
            </span>
          </Typography>

          <Typography className="text-[9px] font-semibold text-gray-600 md:text-[10px]">
            <span data-capture-offset="-7">
              I have hidden your word in my heart.
              <br />
              (Psalm 119:11)
            </span>
          </Typography>
        </div>

        {/* 결과 */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <Typography className="text-[10px] font-bold text-gray-700 md:text-[11px]">
            잘 맞는 암송 짝꿍{' '}
            <span
              className="font-extrabold"
              style={{ color: result.accentColor }}
            >
              {result.bestMatchType}
            </span>
          </Typography>

          <span
            className="shrink-0 rounded-full px-3 py-1.5 text-[10px] font-extrabold text-white"
            style={{
              backgroundColor: result.accentColor,
            }}
          >
            <span data-capture-offset="-7">AI 인증 {totalScore}점 · 합격</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default FreeTrialResultCard;
